import { useRef, useEffect } from 'react';
import type { Language, PixelData } from '@vikings/types';
import { t } from '@vikings/game-data';
import { getLightningPixels, renderPixels } from '@vikings/pixel-engine';
import './PixelModal.css';

interface LightningLoadingOverlayProps {
  language?: Language;
}

interface Spark {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  color: string;
}

interface BoltSegment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  isBranch?: boolean;
}

interface ActiveBolt {
  segments: BoltSegment[];
  life: number;
  maxLife: number;
  spriteX?: number;
  spriteY?: number;
  spriteScale?: number;
}

/**
 * Pixel-line drawing helper for 8-bit retro stepped lightning.
 */
function drawPixelLine(
  ctx: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  ps: number,
  color: string,
  widthPs: number,
) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const dist = Math.hypot(dx, dy);
  const steps = Math.max(1, Math.ceil(dist / ps));
  ctx.fillStyle = color;

  const half = Math.floor(widthPs / 2);
  for (let i = 0; i <= steps; i++) {
    const prog = i / steps;
    const px = Math.round((x1 + dx * prog) / ps) * ps;
    const py = Math.round((y1 + dy * prog) / ps) * ps;
    ctx.fillRect(px - half * ps, py - half * ps, widthPs * ps, widthPs * ps);
  }
}

/**
 * Generate procedural jagged pixel lightning bolt paths.
 */
function generateBolt(w: number, h: number): { segments: BoltSegment[]; strikeX: number; strikeY: number } {
  const segments: BoltSegment[] = [];
  const startX = Math.random() * (w * 0.7) + w * 0.15;
  let curX = startX;
  let curY = 0;
  const targetY = Math.min(h * 0.9, h * 0.35 + Math.random() * (h * 0.5));

  while (curY < targetY) {
    const stepY = Math.min(targetY - curY, Math.random() * 24 + 14);
    const stepX = (Math.random() - 0.5) * 44;
    const nextX = Math.max(20, Math.min(w - 20, curX + stepX));
    const nextY = curY + stepY;

    segments.push({ x1: curX, y1: curY, x2: nextX, y2: nextY });

    // Branch probability
    if (Math.random() < 0.3 && nextY < targetY - 30) {
      let bX = nextX;
      let bY = nextY;
      const dir = Math.random() < 0.5 ? -1 : 1;
      const branchSteps = Math.floor(Math.random() * 3) + 2;
      for (let i = 0; i < branchSteps; i++) {
        const nbY = bY + Math.random() * 18 + 10;
        const nbX = bX + dir * (Math.random() * 26 + 12);
        segments.push({ x1: bX, y1: bY, x2: nbX, y2: nbY, isBranch: true });
        bX = nbX;
        bY = nbY;
      }
    }

    curX = nextX;
    curY = nextY;
  }

  return { segments, strikeX: curX, strikeY: curY };
}

/**
 * Full-screen loading overlay with instant darkening, animated pixel lightning,
 * ambient thunder flashes, sparks, and "loading..." message.
 */
export function LightningLoadingOverlay({ language = 'uk' }: LightningLoadingOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);
  const spriteRef = useRef<PixelData>(getLightningPixels());

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let w = window.innerWidth;
    let h = window.innerHeight;
    const dpr = window.devicePixelRatio || 1;

    const handleResize = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    const boltsRef: ActiveBolt[] = [];
    const sparksRef: Spark[] = [];
    let skyFlashAlpha = 0.55; // Immediate initial thunder flash upon opening!
    let strikeTimer = 0;
    let lastTime = performance.now();

    // Trigger an initial strike immediately on mount
    const triggerStrike = () => {
      skyFlashAlpha = 0.45 + Math.random() * 0.25;
      const boltData = generateBolt(w, h);
      const spriteScale = Math.max(2, Math.floor(Math.min(w, h) / 180));
      boltsRef.push({
        segments: boltData.segments,
        life: 0,
        maxLife: 0.16 + Math.random() * 0.08,
        spriteX: boltData.strikeX - 6 * spriteScale,
        spriteY: Math.max(10, boltData.strikeY - 14 * spriteScale),
        spriteScale,
      });

      // Spawn spark particles at strike point
      for (let i = 0; i < 10; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * 140 + 50;
        sparksRef.push({
          x: boltData.strikeX,
          y: boltData.strikeY,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed - 40,
          life: 0,
          maxLife: 0.15 + Math.random() * 0.2,
          color: Math.random() > 0.3 ? '#ffffff' : '#93c5fd',
        });
      }
    };

    triggerStrike();

    const loop = (now: number) => {
      const delta = Math.min(0.1, (now - lastTime) / 1000);
      lastTime = now;

      // Periodic strikes while loading
      strikeTimer += delta;
      if (strikeTimer > 0.28 + Math.random() * 0.2) {
        strikeTimer = 0;
        triggerStrike();
      }

      // Decay sky flash
      if (skyFlashAlpha > 0) {
        skyFlashAlpha = Math.max(0, skyFlashAlpha - delta * 3.2);
      }

      // ── 1. Clear & Dark Screen Overlay ──
      ctx.clearRect(0, 0, w, h);

      // Deep dark night background darkening
      ctx.fillStyle = 'rgba(8, 12, 24, 0.85)';
      ctx.fillRect(0, 0, w, h);

      // Ambient lightning flash over the dark background
      if (skyFlashAlpha > 0) {
        ctx.fillStyle = `rgba(195, 225, 255, ${skyFlashAlpha * 0.4})`;
        ctx.fillRect(0, 0, w, h);
      }

      // Pixel step scale
      const ps = Math.max(3, Math.floor(Math.min(w, h) / 160));

      // ── 2. Update & Draw Lightning Bolts ──
      for (let i = boltsRef.length - 1; i >= 0; i--) {
        const bolt = boltsRef[i];
        bolt.life += delta;
        if (bolt.life >= bolt.maxLife) {
          boltsRef.splice(i, 1);
          continue;
        }

        // Rapid flicker effect
        const flicker = Math.sin(bolt.life * 70);
        if (flicker < -0.3) continue;

        // Draw outer glow segments
        for (const seg of bolt.segments) {
          drawPixelLine(
            ctx,
            seg.x1,
            seg.y1,
            seg.x2,
            seg.y2,
            ps,
            '#60a5fa',
            seg.isBranch ? 2 : 3,
          );
        }

        // Draw inner white core segments
        for (const seg of bolt.segments) {
          drawPixelLine(
            ctx,
            seg.x1,
            seg.y1,
            seg.x2,
            seg.y2,
            ps,
            '#ffffff',
            1,
          );
        }

        // Draw pixel sprite bolt
        if (bolt.spriteX !== undefined && bolt.spriteY !== undefined && bolt.spriteScale) {
          renderPixels(
            ctx,
            spriteRef.current,
            Math.round(bolt.spriteX),
            Math.round(bolt.spriteY),
            bolt.spriteScale,
          );
        }
      }

      // ── 3. Update & Draw Sparks ──
      for (let i = sparksRef.length - 1; i >= 0; i--) {
        const s = sparksRef[i];
        s.life += delta;
        if (s.life >= s.maxLife) {
          sparksRef.splice(i, 1);
          continue;
        }

        s.x += s.vx * delta;
        s.y += s.vy * delta;
        s.vy += 180 * delta; // Gravity

        const sparkSize = Math.max(2, ps - 1);
        ctx.fillStyle = s.color;
        ctx.fillRect(Math.round(s.x), Math.round(s.y), sparkSize, sparkSize);
      }

      animFrameRef.current = requestAnimationFrame(loop);
    };

    animFrameRef.current = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div
      className="pixel-loading-overlay"
      onClick={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      <canvas ref={canvasRef} className="pixel-lightning-canvas" />
      <div className="pixel-modal pixel-modal-loading">
        <div className="pixel-modal-loading-icon">⚡</div>
        <div className="pixel-modal-title" style={{ fontSize: '18px', letterSpacing: '2px', margin: '4px 0 8px' }}>
          {t(language, 'loading')}
        </div>
        <div className="pixel-loading-dots">
          <span className="pixel-dot" />
          <span className="pixel-dot" />
          <span className="pixel-dot" />
        </div>
      </div>
    </div>
  );
}
